/**
 * RichEditor — contenteditable rich text editor with:
 *   • Formatting: Bold, Italic, Underline, Strikethrough, Headings, Lists, Quote
 *   • Font size: Small / Normal / Large / Huge
 *   • Text & highlight colours (20 presets + custom colour picker)
 *   • Emoji picker (4 categories, ~120 emojis)
 *   • Attachments via the parent-provided onAttach button / paste (images
 *     inline-embed at the cursor; other files defer as attachments)
 *   • @user mentions (dropdown, keyboard nav)
 *   • #task links (dropdown, keyboard nav)
 *
 * Props
 *   onChange(html)   — called on every change
 *   placeholder      — greyed-out hint text
 *   users            — [{ id, first_name, last_name, email }]
 *   tasks            — [{ id, title }]
 *   appId            — workspace app ID (needed for image upload URL)
 *   maxLength        — plain-text character cap; blocks further typing/paste
 *                       once hit, like a native <input maxLength>
 *   richPaste        — when true, pasted HTML (Word/Docs/ChatGPT/spreadsheets)
 *                       keeps its structure + a safe style allowlist (color,
 *                       highlight, weight, italics, underline, tables, links)
 *                       instead of being flattened to plain text
 *
 * Exports getValue() for parent use via ref:
 *   const editorRef = useRef(); ... editorRef.current.getValue()
 */

import React, {
  useState, useEffect, useRef, useCallback, useImperativeHandle, forwardRef,
} from 'react';
import { useSpacesUpload } from '../../lib/useSpacesUpload';
import { AttachmentModal, truncateFileName } from '../shared/AttachmentComponents';
// Imported (not referenced via a public/ URL) so webpack emits a hashed,
// build-resolved path — a plain `url('/assets/...')` in CSS gets resolved
// by css-loader as a module request relative to the CSS file, not as a
// public-root path, and fails the production build.
import linesFrameImg from '../../assets/default_frame.png';
import flowerFrameImg from '../../assets/Onam_Floral_Frame.png';

export const FRAME_IMAGES = { lines: linesFrameImg, flower: flowerFrameImg };

// ─── Emoji catalogue ──────────────────────────────────────────────────────────

const EMOJI_GROUPS = [
  {
    tab: '😊',
    label: 'Reactions',
    emojis: ['😀','😄','😂','🤣','🥰','😍','🤩','😎','🥳','😊','😇','😁','😆','😅',
             '😏','🤔','😮','🥺','😢','😭','😤','😠','😴','🤗','🤫','😙','🥶','🥵',
             '🎉','🙌','👏','🔥','💯','✨','🌟','❤️','💪','👍','🤝','🏅'],
  },
  {
    tab: '💼',
    label: 'Work',
    emojis: ['💡','📊','📈','📉','📝','✅','❌','⏰','📅','🎯','🏆','🥇','🚀','💻',
             '📱','🖥️','📢','🔑','🎓','🔧','⚙️','📌','📋','📣','💬','💎','🔔','🔒',
             '🔓','🗂️','📂','📁','📄','🖨️','⌨️','🖱️','💾','💿','🎙️','📡','🔭','🔬'],
  },
  {
    tab: '🌈',
    label: 'Culture',
    emojis: ['🎊','🎈','🎁','🌈','⭐','💫','🌺','🌸','🌻','🍀','🎵','🎶','🎨',
             '🌙','☀️','🌊','🦋','🌿','🎭','🎬','🎪','🎠','🌍','🏔️','🌅','🌠',
             '🦄','🐉','🦁','🦊','🐯','🦋','🌹','💐','🍁','🍂','🌴','🏖️','⛩️','🎋'],
  },
  {
    tab: '👥',
    label: 'People',
    emojis: ['🙏','👋','🤜','🤛','✌️','🤞','👌','🤙','☝️','👆','👇','👈','👉',
             '👐','🤲','🫶','💁','🙋','🤷','🤦','🧠','💆','🏋️','🤸','🧘','🤸',
             '👨‍💻','👩‍💻','👨‍🏫','👩‍🏫','👨‍💼','👩‍💼','👨‍🔬','👩‍🔬','🧑‍🎨','🧑‍🚀'],
  },
];

// ─── Colour palette ───────────────────────────────────────────────────────────

const COLORS = [
  '#000000','#374151','#6B7280','#9CA3AF','#FFFFFF',
  '#EF4444','#F97316','#EAB308','#22C55E','#3B82F6',
  '#8B5CF6','#EC4899','#14B8A6','#F59E0B','#10B981',
  '#1D4ED8','#7C3AED','#DB2777','#0891B2','#DC2626',
];

// ─── Font sizes ───────────────────────────────────────────────────────────────

const FONT_SIZES = [
  { label: 'Small',  value: '2' },
  { label: 'Normal', value: '3' },
  { label: 'Large',  value: '5' },
  { label: 'Huge',   value: '7' },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function uname(u) {
  return [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email || '';
}

// A representative emoji per broad file category — built imperatively via
// document.createElement (inline chips live inside contenteditable DOM, not
// React), so this stays a plain string lookup rather than an icon component.
function fileEmoji(name, contentType) {
  const ext = (name || '').split('.').pop()?.toLowerCase() || '';
  const ct = contentType || '';
  if (ct.includes('zip') || ['zip', 'rar', '7z', 'tar', 'gz', 'tgz', 'tbz2', 'bz2', 'xz', 'zst'].includes(ext)) return '🗜️';
  if (ct.includes('spreadsheet') || ct.includes('excel') || ['xls', 'xlsx', 'csv', 'tsv'].includes(ext)) return '📊';
  if (ct === 'application/pdf' || ext === 'pdf') return '📕';
  if (['doc', 'docx', 'odt', 'rtf'].includes(ext)) return '📝';
  if (['ppt', 'pptx'].includes(ext)) return '📽️';
  if (['mobi', 'epub'].includes(ext)) return '📖';
  if (['obj', 'stl', 'gltf', 'glb', 'ply', 'step', 'iges', '3mf', 'dxf'].includes(ext)) return '🧊';
  if (['ttf', 'otf', 'woff2', 'eot'].includes(ext)) return '🔤';
  if (['log', 'accesslog', 'syslog', 'trace'].includes(ext)) return '🧾';
  if (['json', 'xml', 'yaml', 'yml', 'sql', 'csv', 'jsonl', 'ndjson', 'toml', 'ini'].includes(ext)) return '🗄️';
  if (['js', 'css', 'html', 'py', 'java', 'go', 'php', 'rb', 'rs', 'sh', 'c', 'cpp'].includes(ext)) return '💻';
  return '📎';
}

function formatFileSize(bytes) {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ─── useOutsideClick ─────────────────────────────────────────────────────────

function useOutsideClick(ref, handler) {
  useEffect(() => {
    function listener(e) { if (ref.current && !ref.current.contains(e.target)) handler(); }
    // defer so the mousedown that opened us doesn't immediately close us
    const t = setTimeout(() => document.addEventListener('mousedown', listener), 0);
    return () => { clearTimeout(t); document.removeEventListener('mousedown', listener); };
  }, [ref, handler]);
}

// ─── Toolbar button ───────────────────────────────────────────────────────────

function TBtn({ title, onClick, active, children, style }) {
  return (
    <button
      title={title}
      onMouseDown={e => { e.preventDefault(); onClick?.(); }}
      style={{
        padding: '4px 7px', border: 'none', borderRadius: 4,
        background: active ? 'var(--brand-light)' : 'transparent',
        color: active ? 'var(--brand)' : 'var(--text-secondary)',
        cursor: 'pointer', fontSize: 13, lineHeight: 1,
        outline: 'none', ...style,
      }}
    >{children}</button>
  );
}

// ─── Separator ────────────────────────────────────────────────────────────────

function Sep() {
  return <span style={{ display: 'inline-block', width: 1, height: 20, background: 'var(--border)', margin: '0 3px', verticalAlign: 'middle' }} />;
}

// ─── Emoji picker ─────────────────────────────────────────────────────────────
// Exported so plain (non-RichEditor) inputs, like the Feed comment box, can
// reuse the same picker instead of duplicating the emoji catalogue + UI.

export function EmojiPicker({ onSelect, onClose, anchorRef }) {
  const ref = useRef(null);
  const [tab, setTab] = useState(0);
  useOutsideClick(ref, onClose);

  // Fixed + recomputed on scroll (capture phase, so it also catches an
  // ancestor's own scroll container, not just window) — position:fixed
  // doesn't track scroll on its own, so a one-time computation leaves the
  // popover stranded at its original screen position once the page (e.g.
  // the Feed list) scrolls the anchor button out from under it. Same
  // recipe as FramePicker below.
  const [pos, setPos] = useState({ top: 200, left: 200 });
  useEffect(() => {
    const update = () => {
      if (!anchorRef?.current) return;
      const r = anchorRef.current.getBoundingClientRect();
      const w = 300;
      const left = Math.min(r.right - w, window.innerWidth - w - 8);
      setPos({ top: r.bottom + 4, left: Math.max(8, left) });
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [anchorRef]);

  return (
    <div ref={ref} style={{
      position: 'fixed', top: pos.top, left: pos.left, zIndex: 9999,
      background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10,
      boxShadow: '0 8px 24px rgba(0,0,0,0.14)', width: 300, padding: 8,
    }}>
      {/* Category tabs */}
      <div style={{ display: 'flex', gap: 2, marginBottom: 6 }}>
        {EMOJI_GROUPS.map((g, i) => (
          <button key={i} onMouseDown={e => { e.preventDefault(); setTab(i); }}
            title={g.label}
            style={{
              flex: 1, padding: '4px 0', border: 'none', cursor: 'pointer', borderRadius: 6, fontSize: 18,
              background: tab === i ? 'var(--brand-light)' : 'transparent',
            }}
          >{g.tab}</button>
        ))}
      </div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4, paddingLeft: 2 }}>
        {EMOJI_GROUPS[tab].label}
      </div>
      {/* Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: 1 }}>
        {EMOJI_GROUPS[tab].emojis.map(e => (
          <button key={e} onMouseDown={ev => { ev.preventDefault(); onSelect(e); }}
            style={{
              fontSize: 18, padding: '4px 2px', border: 'none', cursor: 'pointer',
              background: 'transparent', borderRadius: 4, lineHeight: 1,
              transition: 'background 0.1s',
            }}
            title={e}
          >{e}</button>
        ))}
      </div>
    </div>
  );
}

// ─── Colour picker ────────────────────────────────────────────────────────────

function ColorPicker({ heading, onSelect, onClose }) {
  const ref = useRef(null);
  useOutsideClick(ref, onClose);

  return (
    <div ref={ref} style={{
      position: 'absolute', top: '100%', left: 0, zIndex: 300,
      background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10,
      boxShadow: '0 8px 20px rgba(0,0,0,0.12)', padding: 10, minWidth: 150,
    }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 6 }}>{heading}</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 4, marginBottom: 6 }}>
        {COLORS.map(c => (
          <button key={c} onMouseDown={e => { e.preventDefault(); onSelect(c); }}
            title={c}
            style={{
              width: 24, height: 24, borderRadius: 4, border: `1px solid ${c === '#FFFFFF' ? 'var(--border)' : 'transparent'}`,
              background: c, cursor: 'pointer', padding: 0,
            }}
          />
        ))}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Custom:</span>
        <input type="color" defaultValue="#000000"
          onChange={e => onSelect(e.target.value)}
          style={{ width: 32, height: 24, padding: 0, border: '1px solid var(--border)', borderRadius: 4, cursor: 'pointer' }}
        />
      </div>
    </div>
  );
}

// ─── Post frame picker ────────────────────────────────────────────────────────
// Decorative border around the whole published post — purely a display
// choice on the feed_posts row (see FrameWrap/.feed-frame in Feed.jsx). Not
// selecting one (the default) renders exactly as before.

const FRAME_OPTIONS = [
  { value: null,     label: 'No frame' },
  { value: 'lines',  label: 'Lines' },
  { value: 'flower', label: 'Flower' },
];

function FramePicker({ value, onSelect, onClose, anchorRef }) {
  const ref = useRef(null);
  useOutsideClick(ref, onClose);

  // Fixed + computed from the button's own bounding rect (same recipe as
  // EmojiPicker above) instead of position:absolute inside a position:relative
  // wrapper — that wrapper is a flex item in a wrapping toolbar, so anchoring
  // to *it* put the popover in the wrong place (even off in blank space)
  // whenever the button wrapped to its own row in a narrower modal (e.g. the
  // Poll composer) instead of sitting at the end of a single wide row.
  //
  // Computed once wasn't enough on its own, though: these compose modals
  // scroll internally, and position:fixed doesn't move with that scroll —
  // open the picker, then scroll the form to reach the note editor below
  // the toolbar, and the button slides out from under a popover left
  // stranded at its original screen position. Recompute on every scroll
  // (capture phase, so it also catches the modal's own inner scroll
  // container, not just window) to stay glued to the button.
  const [pos, setPos] = useState({ top: 200, left: 200 });
  useEffect(() => {
    const update = () => {
      if (!anchorRef?.current) return;
      const r = anchorRef.current.getBoundingClientRect();
      const w = 180;
      const left = Math.min(r.right - w, window.innerWidth - w - 8);
      setPos({ top: r.bottom + 4, left: Math.max(8, left) });
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [anchorRef]);

  return (
    <div ref={ref} style={{
      position: 'fixed', top: pos.top, left: pos.left, zIndex: 9999,
      background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10,
      boxShadow: '0 8px 20px rgba(0,0,0,0.12)', padding: 6, minWidth: 170,
    }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', margin: '2px 6px 6px' }}>Post frame</div>
      {FRAME_OPTIONS.map(opt => (
        <button key={opt.label} type="button" onMouseDown={e => { e.preventDefault(); onSelect(opt.value); }}
          style={{
            display: 'flex', alignItems: 'center', gap: 8, width: '100%',
            padding: '6px 8px', border: 'none', borderRadius: 6, cursor: 'pointer',
            background: value === opt.value ? 'var(--brand-light)' : 'transparent',
            color: value === opt.value ? 'var(--brand)' : 'var(--text-primary)',
            fontSize: 13, textAlign: 'left',
          }}
        >
          <span className="re-frame-swatch" style={opt.value ? { backgroundImage: `url(${FRAME_IMAGES[opt.value]})` } : undefined} />
          {opt.label}
        </button>
      ))}
    </div>
  );
}

// ─── Font-size dropdown ───────────────────────────────────────────────────────

function FontSizePicker({ onSelect, onClose }) {
  const ref = useRef(null);
  useOutsideClick(ref, onClose);
  return (
    <div ref={ref} style={{
      position: 'absolute', top: '100%', left: 0, zIndex: 300,
      background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8,
      boxShadow: '0 4px 16px rgba(0,0,0,0.10)', minWidth: 110, overflow: 'hidden',
    }}>
      {FONT_SIZES.map(fs => (
        <button key={fs.value} onMouseDown={e => { e.preventDefault(); onSelect(fs.value); }}
          style={{
            display: 'block', width: '100%', textAlign: 'left',
            padding: '7px 14px', border: 'none', background: 'transparent',
            cursor: 'pointer', color: 'var(--text-primary)',
            fontSize: fs.value === '2' ? 11 : fs.value === '3' ? 13 : fs.value === '5' ? 16 : 22,
          }}
        >{fs.label}</button>
      ))}
    </div>
  );
}

// ─── Mention dropdown ─────────────────────────────────────────────────────────

function MentionDropdown({ state, onSelect }) {
  if (!state || !state.results.length) return null;
  const isUser = state.type === 'user';

  return (
    <div style={{
      position: 'fixed', zIndex: 9000,
      left: Math.min(state.x, window.innerWidth - 310), top: state.y,
      background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8,
      boxShadow: '0 8px 24px rgba(0,0,0,0.14)',
      minWidth: 220, maxWidth: 300, overflow: 'hidden',
    }}>
      <div style={{ padding: '5px 10px', fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', background: 'var(--bg-muted)', borderBottom: '1px solid var(--border)' }}>
        {isUser ? '@ Mention a person' : '# Link a task'}
      </div>
      <div style={{ maxHeight: 240, overflowY: 'auto', overflowX: 'hidden' }}>
        {state.results.map((item, i) => {
          const primary = isUser ? uname(item) : item.title;
          const sub     = isUser ? item.email  : `Task #${item.id}`;
          const active  = i === state.selectedIndex;
          return (
            <div key={item.id}
              onMouseDown={e => { e.preventDefault(); onSelect(item); }}
              style={{
                padding: '8px 12px', cursor: 'pointer',
                background: active ? 'var(--brand-light)' : 'transparent',
                borderBottom: i < state.results.length - 1 ? '1px solid var(--border)' : 'none',
              }}
            >
              <div style={{ fontSize: 13, fontWeight: 600, color: active ? 'var(--brand)' : 'var(--text-primary)' }}>{primary}</div>
              {sub && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1 }}>{sub}</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

const RichEditor = forwardRef(function RichEditor(
  { onChange, placeholder = 'Share something with the team…', users = [], tasks = [], appId, initialValue, onAttach, onFilePaste, noBorder, maxLength, richPaste, frameStyle, onFrameChange },
  outerRef,
) {
  const { uploadFiles: uploadImagesToSpaces } = useSpacesUpload(appId, 'feed');
  const editorRef  = useRef(null);

  const [isEmpty,        setIsEmpty]        = useState(!initialValue);
  const [showEmoji,      setShowEmoji]      = useState(false);
  const [showTextColor,  setShowTextColor]  = useState(false);
  const [showBgColor,    setShowBgColor]    = useState(false);
  const [showFontSize,   setShowFontSize]   = useState(false);
  const [showFramePicker, setShowFramePicker] = useState(false);
  const [mentionState,   setMentionState]   = useState(null);
  const [previewAtt,     setPreviewAtt]     = useState(null);

  const mentionRangeRef  = useRef(null);
  const emojiButtonRef   = useRef(null);
  const frameButtonRef   = useRef(null);
  const savedRangeRef    = useRef(null);

  // Saved content never carries the image remove button (getCleanHtml strips
  // it before every save — see its comment). So an image that was already
  // part of the post before this edit session loads via `initialValue` with
  // no button at all, not a broken one. Images attached fresh in *this*
  // session already get one from buildRemovableImage. Backfill it once on
  // mount so pre-existing inline images are removable during an edit too.
  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    el.querySelectorAll('.feed-inline-img-wrap').forEach(wrapper => {
      if (!wrapper.querySelector('.feed-inline-img-remove')) {
        wrapper.appendChild(makeRemoveBtn('feed-inline-img-remove', 'Remove image'));
      }
    });
    el.querySelectorAll('.feed-inline-video-wrap').forEach(wrapper => {
      if (!wrapper.querySelector('.feed-inline-video-remove')) {
        wrapper.appendChild(makeRemoveBtn('feed-inline-video-remove', 'Remove video'));
      }
    });
    el.querySelectorAll('.cmt-inline-img-wrap').forEach(wrapper => {
      const richChip = wrapper.querySelector('.cmt-inline-file-chip--rich');
      if (richChip) {
        if (!richChip.querySelector('.cmt-inline-file-chip-remove')) {
          richChip.appendChild(makeRemoveBtn('cmt-inline-file-chip-remove', 'Remove file'));
        }
      } else if (!wrapper.querySelector('.cmt-inline-img-remove')) {
        wrapper.appendChild(makeRemoveBtn('cmt-inline-img-remove', 'Remove file'));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveRange = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      const r = sel.getRangeAt(0);
      if (editorRef.current?.contains(r.commonAncestorContainer)) {
        savedRangeRef.current = r.cloneRange();
      }
    }
  };

  const restoreRange = () => {
    const r = savedRangeRef.current;
    if (!r) return;
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(r);
  };

  // After a batch attach (e.g. several files at once), each insertNodeAtCursor
  // call leaves the caret right after that node — force it to the true end
  // once the whole batch is in, so anything typed next lands after the last item.
  const moveCaretToEnd = (el) => {
    if (!el) return;
    const range = document.createRange();
    range.selectNodeContents(el);
    range.collapse(false);
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(range);
    savedRangeRef.current = range.cloneRange();
  };

  // Inserts a node at the last known cursor position and re-saves the range
  // so a subsequent insert lands right after it (keeps multi-file pastes in order).
  const insertNodeAtCursor = (el, node) => {
    el.focus();
    restoreRange();
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      const range = sel.getRangeAt(0);
      range.insertNode(node);
      range.setStartAfter(node);
      range.collapse(true);
      sel.removeAllRanges();
      sel.addRange(range);
      savedRangeRef.current = range.cloneRange();
    } else {
      el.appendChild(node);
    }
  };

  // Syncs React state after a DOM mutation the browser doesn't fire a
  // native `input` event for (e.g. a remove button's own click handler).
  const syncAfterDomChange = (el) => {
    setIsEmpty(!el.textContent?.trim() && !el.querySelector('img'));
    onChange?.(getCleanHtml(el));
  };

  // Delegated click handler for inline image/file remove buttons. Content
  // loaded via `initialValue` (dangerouslySetInnerHTML, e.g. reopening an
  // existing post/poll to edit it) is parsed straight from an HTML string —
  // any per-button addEventListener attached at *creation* time never makes
  // it onto those reconstructed nodes, so their "×" silently does nothing.
  // Listening on the editor container instead works regardless of how the
  // button's node ended up in the DOM.
  //
  // (Clicking/typing before, between, or after an image/video/file used to
  // need a bunch of custom caret-placement JS here — display: block on the
  // wrap wasn't reliably clickable-into. Fixed the actual cause instead:
  // .feed-inline-img-wrap/.feed-inline-video-wrap are now display:
  // inline-block + width: 100% (Feed.css), matching the pattern the task
  // description editor already used — an inline-level wrap keeps adjacent
  // text in the same line-flow as the browser's normal caret handling
  // expects, so native click-to-caret just works with no JS involved, the
  // same way it already did for task descriptions.)
  const handleEditorClick = useCallback((e) => {
    const btn = e.target.closest('.feed-inline-img-remove, .cmt-inline-img-remove, .feed-inline-video-remove, .cmt-inline-file-chip-remove');
    if (!btn) {
      // Clicking a file chip (not its remove button) opens an in-app
      // preview — same modal used everywhere else attachments are shown —
      // with its own Download button, rather than forcing a browser
      // download/new-tab for file types that don't render inline.
      const chip = e.target.closest('.cmt-inline-file-chip');
      const chipWrapper = chip?.closest('.cmt-inline-img-wrap');
      if (chipWrapper?.dataset.fileUrl) {
        setPreviewAtt({
          file_url:     chipWrapper.dataset.fileUrl,
          file_name:    chipWrapper.dataset.fileName,
          content_type: chipWrapper.dataset.contentType,
        });
      }
      return;
    }
    const wrapper = btn.closest('.feed-inline-img-wrap, .cmt-inline-img-wrap, .feed-inline-video-wrap');
    if (!wrapper) return;
    e.preventDefault();
    wrapper.remove();
    syncAfterDomChange(editorRef.current);
  }, [onChange]);

  // The inline image "×" remove button only makes sense while composing —
  // it must never be baked into saved post/task content (it'd render as a
  // dead, unstyled "×" below the image once `.rich-post-content *` forces
  // position:static on the read-only feed view). Strip it from any HTML
  // reported upstream via onChange/getValue while leaving the live editable
  // DOM (and the button's own click handler) untouched.
  const isImgWrap = (n) => n?.nodeType === Node.ELEMENT_NODE &&
    (n.classList?.contains('feed-inline-img-wrap') || n.classList?.contains('cmt-inline-img-wrap') ||
     n.classList?.contains('feed-inline-video-wrap'));

  const getCleanHtml = (el) => {
    if (!el) return '';
    const clone = el.cloneNode(true);
    clone.querySelectorAll('.feed-inline-img-remove, .feed-inline-video-remove, .cmt-inline-img-remove, .cmt-inline-file-chip-remove').forEach(btn => btn.remove());
    return clone.innerHTML;
  };

  // Flattens pasted HTML down to plain text — no formatting ever survives —
  // while still turning paragraph/list/table-row boundaries into line breaks
  // and table cells into tab stops, so structure isn't lost along with style.
  const htmlToPlainText = (root) => {
    const BLOCK_TAGS = new Set(['P', 'DIV', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'LI', 'TR', 'BLOCKQUOTE', 'HR']);
    let text = '';
    const walk = (node) => {
      if (node.nodeType === Node.TEXT_NODE) { text += node.textContent; return; }
      if (node.nodeName === 'BR') { text += '\n'; return; }
      if (node.nodeName === 'TD' || node.nodeName === 'TH') {
        node.childNodes.forEach(walk);
        text += '\t';
        return;
      }
      node.childNodes.forEach(walk);
      if (BLOCK_TAGS.has(node.nodeName)) text += '\n';
    };
    root.childNodes.forEach(walk);
    return text.replace(/[ \t]+\n/g, '\n').replace(/\n{3,}/g, '\n\n').trim();
  };

  // Rebuilds pasted HTML (from Word, Google Docs, ChatGPT, a spreadsheet, ...)
  // keeping structure (tables, headings, lists, links) and a small allowlist
  // of inline style properties (text color, highlight, weight, italics,
  // underline) so a pasted table/colored text looks the same as the source —
  // while still stripping everything else (scripts, classes, Word's own
  // mso- layout junk, arbitrary inline styles) so the source's page layout
  // can never leak in. Used only where richPaste is explicitly enabled.
  const RICH_PASTE_TAGS = new Set([
    'P', 'DIV', 'BR', 'B', 'STRONG', 'I', 'EM', 'U', 'S', 'STRIKE',
    'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'UL', 'OL', 'LI', 'SPAN', 'FONT', 'A',
    'BLOCKQUOTE', 'TABLE', 'THEAD', 'TBODY', 'TFOOT', 'TR', 'TD', 'TH',
  ]);
  // Dropped entirely (subtree and all) — these hold raw CSS/script/metadata
  // text as their "content", not real paste text. Word's clipboard HTML in
  // particular ships a <style> block full of @font-face/mso- CSS; treating
  // it as just "an unknown wrapper tag" (like every other stripped tag) would
  // keep walking into its text node and paste the raw CSS as visible content.
  const RICH_PASTE_DROP_TAGS = new Set(['STYLE', 'SCRIPT', 'HEAD', 'META', 'LINK', 'TITLE', 'NOSCRIPT', 'XML']);
  const RICH_PASTE_STYLE_PROPS = ['color', 'background-color', 'font-weight', 'font-style', 'text-decoration'];
  const sanitizeRichPaste = (root) => {
    const clean = (node) => {
      if (node.nodeType === Node.COMMENT_NODE) return null;
      if (RICH_PASTE_DROP_TAGS.has(node.nodeName)) return null;
      if (node.nodeType === Node.TEXT_NODE) return document.createTextNode(node.textContent);
      if (node.nodeName === 'BR') return document.createElement('br');
      if (RICH_PASTE_TAGS.has(node.nodeName)) {
        const el = document.createElement(node.nodeName.toLowerCase());
        if (node.nodeName === 'A' && node.getAttribute('href')) {
          el.setAttribute('href', node.getAttribute('href'));
          el.setAttribute('target', '_blank');
          el.setAttribute('rel', 'noopener noreferrer');
        }
        if (node.style && node.style.length) {
          const kept = RICH_PASTE_STYLE_PROPS
            .map(prop => [prop, node.style.getPropertyValue(prop)])
            .filter(([, val]) => val)
            .map(([prop, val]) => `${prop}: ${val}`);
          if (kept.length) el.setAttribute('style', kept.join('; '));
        }
        node.childNodes.forEach(child => {
          const c = clean(child);
          if (c) el.appendChild(c);
        });
        return el;
      }
      const frag = document.createDocumentFragment();
      node.childNodes.forEach(child => {
        const c = clean(child);
        if (c) frag.appendChild(c);
      });
      return frag;
    };
    return clean(root);
  };

  // Wraps an already-uploaded image/video in a removable span (small "×"
  // button, like every other attachment preview in the app) so it can be
  // cancelled after insertion instead of being permanently stuck in the content.
  const makeRemoveBtn = (className, title) => {
    const removeBtn = document.createElement('button');
    removeBtn.type = 'button';
    removeBtn.className = className;
    removeBtn.title = title;
    removeBtn.textContent = '×';
    // No addEventListener here — clicks are handled by the delegated
    // handleEditorClick on the editor container (see its comment for why).
    return removeBtn;
  };

  const buildRemovableImage = (el, src, alt) => {
    const wrapper = document.createElement('span');
    wrapper.className = 'feed-inline-img-wrap';
    wrapper.contentEditable = 'false';
    const img = document.createElement('img');
    img.src = src;
    img.alt = alt;
    // alt isn't shown as a hover tooltip once the image loads (only a
    // broken-image fallback) — title is what actually triggers one.
    img.title = alt;
    img.className = 'feed-inline-img';
    wrapper.appendChild(img);
    wrapper.appendChild(makeRemoveBtn('feed-inline-img-remove', 'Remove image'));
    return wrapper;
  };

  const buildRemovableVideo = (el, src, name) => {
    const wrapper = document.createElement('span');
    wrapper.className = 'feed-inline-video-wrap';
    wrapper.contentEditable = 'false';
    const video = document.createElement('video');
    video.src = src;
    video.controls = true;
    video.className = 'feed-inline-video';
    if (name) video.title = name;
    wrapper.appendChild(video);
    wrapper.appendChild(makeRemoveBtn('feed-inline-video-remove', 'Remove video'));
    return wrapper;
  };

  // Non-image/video files also upload immediately and embed inline as a
  // clickable chip at the cursor, same as buildRemovableImage/Video — the
  // file's URL/name live in data attributes so the chip is fully
  // self-describing and survives being saved as HTML and reloaded, unlike
  // the old approach of stashing a live File reference that only existed
  // for the current browser session.
  const buildRemovableFileChip = (el, uploaded) => {
    const wrapper = document.createElement('span');
    wrapper.className = 'cmt-inline-img-wrap';
    wrapper.contentEditable = 'false';
    wrapper.dataset.fileUrl = uploaded.file_url;
    wrapper.dataset.fileName = uploaded.file_name;
    wrapper.dataset.contentType = uploaded.content_type || '';

    const chip = document.createElement('span');
    // "--rich" modifier keeps this icon+name+size layout scoped to Feed's
    // chip only — task comments (CommentsPanel.jsx) reuse the base
    // .cmt-inline-file-chip class for a plain text-only chip and must keep
    // looking exactly as before.
    chip.className = 'cmt-inline-file-chip cmt-inline-file-chip--rich';

    const icon = document.createElement('span');
    icon.className = 'cmt-inline-file-chip-icon';
    icon.textContent = fileEmoji(uploaded.file_name, uploaded.content_type);
    chip.appendChild(icon);

    const body = document.createElement('span');
    body.className = 'cmt-inline-file-chip-body';
    const nameSpan = document.createElement('span');
    nameSpan.className = 'cmt-inline-file-chip-name';
    nameSpan.textContent = truncateFileName(uploaded.file_name, 18);
    nameSpan.title = uploaded.file_name;
    body.appendChild(nameSpan);
    if (uploaded.file_size != null) {
      const sizeSpan = document.createElement('span');
      sizeSpan.className = 'cmt-inline-file-chip-size';
      sizeSpan.textContent = formatFileSize(uploaded.file_size);
      body.appendChild(sizeSpan);
    }
    chip.appendChild(body);
    // Sits inline as the chip's last flex item (like the reference design),
    // not as an absolutely-positioned badge floating above the corner —
    // that corner-badge style is reserved for images/videos below.
    chip.appendChild(makeRemoveBtn('cmt-inline-file-chip-remove', 'Remove file'));

    wrapper.appendChild(chip);
    return wrapper;
  };

  // Placeholder shown the instant a file is picked, before its upload
  // finishes — a local blob preview (image/video) or a "Uploading…" chip,
  // swapped in place for the real node once that file's own upload
  // resolves. Lets a multi-file attach show every file at once instead of
  // only appearing one by one as each upload completes in turn.
  const buildLoadingPlaceholder = (kind, file) => {
    const spinner = () => {
      const s = document.createElement('span');
      s.className = 're-spinner';
      return s;
    };
    if (kind === 'image' || kind === 'video') {
      const wrapper = document.createElement('span');
      wrapper.className = kind === 'image' ? 'feed-inline-img-wrap' : 'feed-inline-video-wrap';
      wrapper.contentEditable = 'false';
      const media = document.createElement(kind === 'image' ? 'img' : 'video');
      media.src = URL.createObjectURL(file);
      media.className = kind === 'image' ? 'feed-inline-img' : 'feed-inline-video';
      if (kind === 'image') media.alt = file.name; else media.muted = true;
      wrapper.appendChild(media);
      const overlay = document.createElement('span');
      overlay.className = 're-loading-overlay';
      overlay.appendChild(spinner());
      wrapper.appendChild(overlay);
      return wrapper;
    }
    const wrapper = document.createElement('span');
    wrapper.className = 'cmt-inline-img-wrap';
    wrapper.contentEditable = 'false';
    const chip = document.createElement('span');
    chip.className = 'cmt-inline-file-chip cmt-inline-file-chip--rich';
    const icon = document.createElement('span');
    icon.className = 'cmt-inline-file-chip-icon';
    icon.appendChild(spinner());
    chip.appendChild(icon);
    const body = document.createElement('span');
    body.className = 'cmt-inline-file-chip-body';
    const nameSpan = document.createElement('span');
    nameSpan.className = 'cmt-inline-file-chip-name';
    nameSpan.textContent = truncateFileName(file.name, 18);
    nameSpan.title = file.name;
    body.appendChild(nameSpan);
    const sizeSpan = document.createElement('span');
    sizeSpan.className = 'cmt-inline-file-chip-size';
    sizeSpan.textContent = 'Uploading…';
    body.appendChild(sizeSpan);
    chip.appendChild(body);
    wrapper.appendChild(chip);
    return wrapper;
  };

  // Images, videos, and every other file type all upload and embed inline
  // at the cursor — same path as the toolbar image button — so they land
  // exactly where the user placed them, and the compose view (and the
  // saved post) both stay a truthful, consistent preview of what was
  // actually attached, regardless of file type. Every file gets its
  // placeholder inserted up front (in order), then all of them upload
  // concurrently — a batch attach shows every file immediately with its
  // own spinner rather than appearing one at a time as each finishes.
  const insertFiles = async (files) => {
    const el = editorRef.current;
    if (!el || !files.length) return;
    setIsEmpty(false);

    const jobs = files.map(file => {
      const kind = file.type?.startsWith('image/') ? 'image' : file.type?.startsWith('video/') ? 'video' : 'file';
      const placeholder = buildLoadingPlaceholder(kind, file);
      insertNodeAtCursor(el, placeholder);
      return { file, kind, placeholder };
    });
    moveCaretToEnd(el);
    onChange?.(getCleanHtml(el));

    await Promise.allSettled(jobs.map(async ({ file, kind, placeholder }) => {
      try {
        const [uploaded] = await uploadImagesToSpaces([file]);
        const real = kind === 'image' ? buildRemovableImage(el, uploaded.file_url, file.name)
          : kind === 'video' ? buildRemovableVideo(el, uploaded.file_url, file.name)
          : buildRemovableFileChip(el, uploaded);
        placeholder.replaceWith(real);
      } catch (err) {
        placeholder.remove();
        alert(err.message || `"${file.name}" upload failed`);
      }
      onChange?.(getCleanHtml(el));
    }));
  };

  // Every file type now uploads and embeds inline via insertFiles, so
  // nothing ever sets a live `_file` reference on a node anymore — this is
  // a permanent no-op, kept only so existing callers (which still combine
  // its result with any pre-existing `attachments` on submit) don't need
  // to change.
  const extractFiles = () => {
    const el = editorRef.current;
    if (!el) return [];
    const files = [];
    const wrappers = [];
    const walk = (node) => {
      if (node._file instanceof File) { files.push(node._file); wrappers.push(node); }
      else { node.childNodes.forEach(walk); }
    };
    el.childNodes.forEach(walk);
    wrappers.forEach(w => w.remove());
    if (wrappers.length) setIsEmpty(!el.textContent?.trim() && !el.querySelector('img'));
    return files;
  };

  // Expose getValue(), clear(), insertFiles(), extractFiles() to parent via ref
  useImperativeHandle(outerRef, () => ({
    getValue:     () => getCleanHtml(editorRef.current),
    clear:        () => { if (editorRef.current) { editorRef.current.innerHTML = ''; setIsEmpty(true); } },
    insertFiles,
    extractFiles,
    saveRange,
  }));

  // Close all popups except the specified one
  const closeAll = (except = null) => {
    if (except !== 'emoji')     setShowEmoji(false);
    if (except !== 'textColor') setShowTextColor(false);
    if (except !== 'bgColor')   setShowBgColor(false);
    if (except !== 'fontSize')  setShowFontSize(false);
    if (except !== 'frame')     setShowFramePicker(false);
  };

  // Execute a document command while keeping editor focused
  const exec = useCallback((cmd, arg = null) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, arg);
    const html = editorRef.current?.innerHTML || '';
    setIsEmpty(!html || html === '<br>');
    onChange?.(getCleanHtml(editorRef.current));
  }, [onChange]);

  // ── Mention detection ────────────────────────────────────────────────────────
  const detectMention = useCallback(() => {
    const sel = window.getSelection();
    if (!sel?.rangeCount) return;

    const range = sel.getRangeAt(0);
    const node  = range.startContainer;
    if (node.nodeType !== Node.TEXT_NODE) { setMentionState(null); return; }

    const textBefore = node.textContent.substring(0, range.startOffset);
    const atMatch    = textBefore.match(/(?:^|[\s,])(@\w*)$/);
    const hashMatch  = textBefore.match(/(?:^|[\s,])(#\w*)$/);
    const found      = atMatch || hashMatch;

    if (!found) { setMentionState(null); mentionRangeRef.current = null; return; }

    const isUser = !!atMatch;
    const match  = isUser ? atMatch : hashMatch;
    const query  = match[1].substring(1).toLowerCase();   // strip trigger char

    // Save range covering the trigger text (for later replacement)
    const tRange = document.createRange();
    tRange.setStart(node, range.startOffset - match[1].length);
    tRange.setEnd(node, range.startOffset);
    mentionRangeRef.current = tRange;

    // Dropdown position — use fixed viewport coords so scrolling doesn't stale them
    const cursorRect = range.getBoundingClientRect();
    const x = cursorRect.left;
    const y = cursorRect.bottom + 4;

    const results = isUser
      ? users.filter(u => {
          const n = uname(u).toLowerCase();
          return n.includes(query) || (u.email || '').toLowerCase().includes(query);
        }).slice(0, 20)
      : tasks.filter(t => (t.title || '').toLowerCase().includes(query)).slice(0, 20);

    setMentionState({ type: isUser ? 'user' : 'task', query, results, selectedIndex: 0, x, y });
  }, [users, tasks]);

  // ── Insert mention ────────────────────────────────────────────────────────────
  const insertMention = useCallback((item) => {
    const tRange = mentionRangeRef.current;
    if (!tRange) return;

    editorRef.current?.focus();
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(tRange);

    const isUser = mentionState?.type === 'user';
    const html = isUser
      ? `<span class="mention-user" contenteditable="false" data-user-id="${item.id}"` +
        ` style="color:#1d4ed8;font-weight:600;background:#dbeafe;padding:1px 6px;border-radius:4px;cursor:default;">@${uname(item)}</span>&nbsp;`
      : `<a class="mention-task" data-task-id="${item.id}"` +
        ` href="/apps/${appId}/work/tasks/${item.id}"` +
        ` style="color:#059669;font-weight:600;background:#d1fae5;padding:1px 6px;border-radius:4px;text-decoration:none;">#${item.title}</a>&nbsp;`;

    document.execCommand('insertHTML', false, html);
    setMentionState(null);
    mentionRangeRef.current = null;
    setIsEmpty(false);
    onChange?.(getCleanHtml(editorRef.current));
  }, [mentionState, appId, onChange]);

  // ── onPaste ───────────────────────────────────────────────────────────────────
  // Default behavior: insert as plain text, never the source's formatting.
  // Pasted content used to keep a small set of "safe" tags (bold, headings,
  // tables, ...) with their styling stripped — but plenty of copied text
  // (a page heading, a styled UI label, a Google Docs paragraph) is wrapped
  // in one of those tags purely for the source's own layout, not because
  // the user wants bold/heading text here. Simplest fix that can't leak any
  // formatting: convert to plain text, preserving line breaks between
  // paragraphs/list items/table rows (and tabs between table cells) so
  // structure survives even though styling never does.
  // When richPaste is on (Feed's Post composer), sanitizeRichPaste below runs
  // first instead, keeping structure + a safe style allowlist.
  const handlePaste = useCallback((e) => {
    e.preventDefault();
    const items = Array.from(e.clipboardData?.items || []);
    const pastedFiles = items.filter(i => i.kind === 'file').map(i => i.getAsFile()).filter(Boolean);
    if (pastedFiles.length) {
      if (onFilePaste) onFilePaste(pastedFiles);
      return;
    }
    const html = e.clipboardData.getData('text/html');

    if (richPaste && html) {
      const tmp = document.createElement('div');
      tmp.innerHTML = html;
      const wrap = document.createElement('div');
      wrap.appendChild(sanitizeRichPaste(tmp));
      if (wrap.textContent?.trim() || wrap.querySelector('table')) {
        document.execCommand('insertHTML', false, wrap.innerHTML);
        const updated = editorRef.current?.innerHTML || '';
        setIsEmpty(!updated || updated === '<br>');
        onChange?.(getCleanHtml(editorRef.current));
        return;
      }
    }

    let text;
    if (html) {
      const tmp = document.createElement('div');
      tmp.innerHTML = html;
      text = htmlToPlainText(tmp);
    } else {
      text = e.clipboardData.getData('text/plain');
    }
    if (maxLength != null) {
      const remaining = Math.max(0, maxLength - (editorRef.current?.textContent?.length || 0));
      text = text.slice(0, remaining);
    }
    document.execCommand('insertText', false, text);
    // Belt-and-suspenders: execCommand('insertText') with a large block can
    // leave some browsers over the cap despite the pre-slice above (e.g. the
    // remaining-budget read racing the DOM's own paste handling). Hard-trim
    // any excess from the end, character node by character node, so inline
    // images/videos elsewhere in the content are never touched.
    if (maxLength != null && editorRef.current) {
      let over = (editorRef.current.textContent?.length || 0) - maxLength;
      if (over > 0) {
        const walker = document.createTreeWalker(editorRef.current, NodeFilter.SHOW_TEXT);
        const textNodes = [];
        let n;
        while ((n = walker.nextNode())) textNodes.push(n);
        for (let i = textNodes.length - 1; i >= 0 && over > 0; i--) {
          const node = textNodes[i];
          const len = node.textContent.length;
          const cut = Math.min(len, over);
          node.textContent = node.textContent.slice(0, len - cut);
          over -= cut;
        }
      }
    }
    const updated = editorRef.current?.innerHTML || '';
    setIsEmpty(!updated || updated === '<br>');
    onChange?.(getCleanHtml(editorRef.current));
  }, [onChange, maxLength, richPaste]);

  // ── onInput ───────────────────────────────────────────────────────────────────
  const handleInput = useCallback(() => {
    const html = editorRef.current?.innerHTML || '';
    setIsEmpty(!html || html === '<br>');
    onChange?.(getCleanHtml(editorRef.current));
    detectMention();
  }, [onChange, detectMention]);

  // ── onKeyDown ─────────────────────────────────────────────────────────────────
  const handleKeyDown = useCallback((e) => {
    // Mention navigation
    if (mentionState?.results.length) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setMentionState(s => ({ ...s, selectedIndex: Math.min(s.selectedIndex + 1, s.results.length - 1) }));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setMentionState(s => ({ ...s, selectedIndex: Math.max(s.selectedIndex - 1, 0) }));
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        insertMention(mentionState.results[mentionState.selectedIndex]);
        return;
      }
      if (e.key === 'Escape') { setMentionState(null); return; }
    }

    // Keyboard shortcuts
    if (e.ctrlKey || e.metaKey) {
      const map = { b: 'bold', i: 'italic', u: 'underline' };
      if (map[e.key]) { e.preventDefault(); exec(map[e.key]); }
      return;
    }

    // Backspace/Delete right at the edge of an inline image/video/file chip
    // (a contentEditable="false" atomic node) is unreliable across browsers —
    // sometimes it takes two presses, sometimes it silently does nothing
    // without firing a proper `input` event to sync React state. Handle it
    // explicitly: if the adjacent node in the delete direction is one of our
    // wraps, remove it directly and sync state ourselves.
    if ((e.key === 'Backspace' || e.key === 'Delete') && !e.shiftKey) {
      const sel = window.getSelection();
      if (sel && sel.isCollapsed && sel.rangeCount) {
        const range = sel.getRangeAt(0);
        const { startContainer, startOffset } = range;

        // Backspace at the very start of the editor's content has nothing
        // before it to delete. Block it outright instead of letting the
        // browser's native contenteditable merge/delete run there — that
        // native path is what was clearing the whole message when the
        // caret sat at position 0, rather than doing nothing as expected.
        if (e.key === 'Backspace' && editorRef.current) {
          const startCheck = document.createRange();
          startCheck.selectNodeContents(editorRef.current);
          startCheck.setEnd(startContainer, startOffset);
          if (startCheck.toString().length === 0 && startCheck.cloneContents().childNodes.length === 0) {
            e.preventDefault();
            return;
          }
        }

        let target = null;
        if (e.key === 'Backspace') {
          if (startContainer.nodeType === Node.TEXT_NODE && startOffset === 0) {
            target = startContainer.previousSibling;
          } else if (startContainer.nodeType === Node.ELEMENT_NODE && startOffset > 0) {
            target = startContainer.childNodes[startOffset - 1];
          }
        } else {
          const textLen = startContainer.nodeType === Node.TEXT_NODE ? startContainer.textContent.length : null;
          if (startContainer.nodeType === Node.TEXT_NODE && startOffset === textLen) {
            target = startContainer.nextSibling;
          } else if (startContainer.nodeType === Node.ELEMENT_NODE) {
            target = startContainer.childNodes[startOffset];
          }
        }
        if (isImgWrap(target)) {
          e.preventDefault();
          target.remove();
          syncAfterDomChange(editorRef.current);
          return;
        }
      }
    }

    // Hard stop at maxLength, like a plain <input maxLength> — block single
    // printable-character keys once the limit is hit, but keep allowing
    // Backspace/Delete/arrows/etc., and allow replacing a selection (which
    // can only shrink or hold the length steady, never grow it).
    if (maxLength != null && e.key.length === 1 && window.getSelection()?.isCollapsed) {
      const currentLen = editorRef.current?.textContent?.length || 0;
      if (currentLen >= maxLength) e.preventDefault();
    }
  }, [mentionState, insertMention, exec, maxLength]);

  return (
    <div style={{ border: noBorder ? 'none' : '1px solid var(--border)', borderRadius: 8, background: 'var(--bg-card)', overflow: 'visible' }}>
      {previewAtt && <AttachmentModal attachment={previewAtt} onClose={() => setPreviewAtt(null)} />}

      {/* ── Toolbar ─────────────────────────────────────────────────────────── */}
      {/* Corner radius always matches the outer box, even when noBorder skips
          the border line — noBorder means "nested in an already-bordered
          wrapper" (see .feed-editor-with-attachments), and that wrapper's
          own corners are rounded with overflow:visible (so the @mention
          dropdown isn't clipped), so a square toolbar/editor here would
          visibly poke its corners out past the wrapper's rounded border. */}
      <div style={{
        display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1,
        padding: '6px 8px', borderBottom: '1px solid var(--border)',
        background: 'var(--bg-muted)', borderRadius: '8px 8px 0 0',
      }}>
        {/* Text formatting */}
        <TBtn title="Bold (Ctrl+B)"          onClick={() => exec('bold')}><b>B</b></TBtn>
        <TBtn title="Italic (Ctrl+I)"         onClick={() => exec('italic')}><i>I</i></TBtn>
        <TBtn title="Underline (Ctrl+U)"      onClick={() => exec('underline')} style={{ textDecoration: 'underline' }}>U</TBtn>
        <TBtn title="Strikethrough"           onClick={() => exec('strikeThrough')} style={{ textDecoration: 'line-through' }}>S</TBtn>

        <Sep />

        {/* Block formats */}
        <TBtn title="Heading"                 onClick={() => exec('formatBlock', 'h2')}>H₁</TBtn>
        <TBtn title="Sub-heading"             onClick={() => exec('formatBlock', 'h3')}>H₂</TBtn>
        <TBtn title="Bullet list"             onClick={() => exec('insertUnorderedList')}>• ─</TBtn>
        <TBtn title="Numbered list"           onClick={() => exec('insertOrderedList')}>1.</TBtn>
        <TBtn title="Block quote"             onClick={() => exec('formatBlock', 'blockquote')}>❝</TBtn>

        <Sep />

        {/* Font size — hidden until execCommand font rendering is fixed */}
        {false && <div style={{ position: 'relative' }}>
          <TBtn title="Font size" active={showFontSize}
            onClick={() => { closeAll('fontSize'); setShowFontSize(v => !v); }}>
            Aa ▾
          </TBtn>
          {showFontSize && (
            <FontSizePicker
              onSelect={v => { exec('fontSize', v); setShowFontSize(false); }}
              onClose={() => setShowFontSize(false)}
            />
          )}
        </div>}

        {/* Text colour — hidden until execCommand font rendering is fixed */}
        {false && <div style={{ position: 'relative' }}>
          <TBtn title="Text colour" active={showTextColor}
            onClick={() => { closeAll('textColor'); setShowTextColor(v => !v); }}>
            <span>A</span>
            <span style={{ display: 'block', height: 3, background: '#ef4444', borderRadius: 1, marginTop: 1 }} />
          </TBtn>
          {showTextColor && (
            <ColorPicker heading="Text Colour"
              onSelect={c => { exec('foreColor', c); setShowTextColor(false); }}
              onClose={() => setShowTextColor(false)}
            />
          )}
        </div>}

        {/* Highlight colour — hidden until execCommand font rendering is fixed */}
        {false && <div style={{ position: 'relative' }}>
          <TBtn title="Highlight" active={showBgColor}
            onClick={() => { closeAll('bgColor'); setShowBgColor(v => !v); }}>
            <span style={{ background: '#fde68a', padding: '0 2px', borderRadius: 2 }}>A</span>
            <span style={{ fontSize: 9, marginLeft: 1 }}>▾</span>
          </TBtn>
          {showBgColor && (
            <ColorPicker heading="Highlight Colour"
              onSelect={c => { exec('hiliteColor', c); setShowBgColor(false); }}
              onClose={() => setShowBgColor(false)}
            />
          )}
        </div>}

        <Sep />

        {/* Emoji picker */}
        <div ref={emojiButtonRef} style={{ position: 'relative', display: 'inline-block' }}>
          <TBtn title="Insert emoji" active={showEmoji}
            onClick={() => { closeAll('emoji'); setShowEmoji(v => !v); }}>
            😊
          </TBtn>
          {showEmoji && (
            <EmojiPicker
              anchorRef={emojiButtonRef}
              onSelect={emoji => {
                editorRef.current?.focus();
                document.execCommand('insertText', false, emoji);
                setIsEmpty(false); onChange?.(getCleanHtml(editorRef.current));
                setShowEmoji(false);
              }}
              onClose={() => setShowEmoji(false)}
            />
          )}
        </div>

        {/* @mention shortcut */}
        {users.length > 0 && (
          <TBtn title="Mention a person (@)"
            onClick={() => {
              editorRef.current?.focus();
              document.execCommand('insertText', false, '@');
              handleInput();
            }}>
            @
          </TBtn>
        )}

        {/* #task shortcut */}
        {tasks.length > 0 && (
          <TBtn title="Link a task (#)"
            onClick={() => {
              editorRef.current?.focus();
              document.execCommand('insertText', false, '#');
              handleInput();
            }}>
            #
          </TBtn>
        )}

        <Sep />

        {/* Clear formatting */}
        <TBtn title="Remove formatting" style={{ fontSize: 11, color: '#9ca3af' }}
          onClick={() => exec('removeFormat')}>
          ✕ fmt
        </TBtn>

        {/* Attach file — only when parent provides handler */}
        {onAttach && (
          <>
            <Sep />
            <TBtn title="Attach file" onClick={onAttach} style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
              📎
            </TBtn>
          </>
        )}

        {/* Post frame — only when parent provides handler */}
        {onFrameChange && (
          <>
            <Sep />
            <div ref={frameButtonRef} style={{ position: 'relative' }}>
              <TBtn title="Post frame" active={showFramePicker || !!frameStyle}
                onClick={() => { closeAll('frame'); setShowFramePicker(v => !v); }} style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                🖼️
              </TBtn>
              {showFramePicker && (
                <FramePicker
                  value={frameStyle || null}
                  onSelect={v => { onFrameChange(v); setShowFramePicker(false); }}
                  onClose={() => setShowFramePicker(false)}
                  anchorRef={frameButtonRef}
                />
              )}
            </div>
          </>
        )}
      </div>

      {/* ── Editor area ──────────────────────────────────────────────────────── */}
      <div style={{ position: 'relative' }}>
        {/* Placeholder */}
        {isEmpty && (
          <div style={{
            position: 'absolute', top: 10, left: 12,
            color: 'var(--text-muted)', fontSize: 14, pointerEvents: 'none', userSelect: 'none',
            lineHeight: 1.6,
          }}>
            {placeholder}
          </div>
        )}

        <div
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          onInput={handleInput}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onClick={handleEditorClick}
          onBlur={saveRange}
          onMouseUp={saveRange}
          onKeyUp={saveRange}
          dangerouslySetInnerHTML={initialValue ? { __html: initialValue } : undefined}
          style={{
            minHeight: 140, maxHeight: 420,
            overflowY: 'auto',
            padding: '10px 12px',
            outline: 'none',
            fontSize: 14, lineHeight: 1.7,
            color: 'var(--text-primary)',
            borderRadius: '0 0 8px 8px',
          }}
        />

        {/* Mention dropdown */}
        <MentionDropdown state={mentionState} onSelect={insertMention} />
      </div>

      {/* ── Inline styles for rendered content ───────────────────────────────── */}
      <style>{`
        [contenteditable] blockquote {
          border-left: 3px solid var(--border);
          margin: 8px 0;
          padding: 4px 12px;
          color: var(--text-muted);
        }
        [contenteditable] h2 { font-size: 20px; margin: 8px 0 4px; }
        [contenteditable] h3 { font-size: 16px; margin: 6px 0 4px; }
        [contenteditable] ul, [contenteditable] ol { padding-left: 22px; margin: 4px 0; }
        [contenteditable] img { max-width: 100%; border-radius: 6px; }
        [contenteditable] video { max-width: 100%; border-radius: 6px; }
        [contenteditable] table { border-collapse: collapse; width: 100%; margin: 8px 0; font-size: 13px; }
        [contenteditable] th, [contenteditable] td { border: 1px solid var(--border, #e2e8f0); padding: 6px 12px; text-align: left; }
        [contenteditable] th { background: var(--bg-secondary, #f8fafc); font-weight: 600; }

        @keyframes re-spin { to { transform: rotate(360deg); } }
        .re-spinner {
          display: inline-block; width: 14px; height: 14px;
          border: 2px solid rgba(120,120,120,0.25); border-top-color: var(--brand, #2563eb);
          border-radius: 50%; animation: re-spin 0.7s linear infinite;
          vertical-align: middle;
        }
        .re-loading-overlay {
          position: absolute; inset: 0; border-radius: 6px;
          display: flex; align-items: center; justify-content: center;
          background: rgba(0,0,0,0.35);
        }
        .re-loading-overlay .re-spinner {
          width: 22px; height: 22px; border-width: 3px;
          border-color: rgba(255,255,255,0.35); border-top-color: #fff;
        }
        .re-frame-swatch {
          width: 20px; height: 20px; border-radius: 5px; flex-shrink: 0;
          border: 1px solid var(--border);
          background-color: var(--bg-secondary, #f1f5f9);
          /* Zoom into the top-left corner, not a center crop — the artwork
             is a thin decorated ring around a mostly-blank interior, so a
             centered crop would show nothing but empty space. */
          background-size: 70px 95px;
          background-position: top left;
          background-repeat: no-repeat;
        }
      `}</style>
    </div>
  );
});

export default RichEditor;
